import type { AttributeCode } from '../state/attributeCode';
import type { CheckboxFilterValue } from '../state/checkbox';
import { CheckboxFilterStateMap } from '../state/checkbox';
import { extractCheckboxCode, isCheckboxParam, prefixCheckbox } from './urlPrefixes';
import { Exit, HashMap, Schema, SchemaGetter } from 'effect';

const BooleanFromString = Schema.Literals(['true', 'false']).pipe(
  Schema.decodeTo(Schema.Boolean, {
    decode: SchemaGetter.transform((literal) => literal === 'true'),
    encode: SchemaGetter.transform((bool) => (bool ? 'true' : 'false')),
  })
);

const checkboxFilterStateMapToUrlRecord = (
  filterStateMap: CheckboxFilterStateMap
): Record<string, string> =>
  HashMap.reduce(filterStateMap, {} as Record<string, string>, (acc, value, attributeCode) => {
    const key = prefixCheckbox(attributeCode);
    acc[key] = Schema.encodeSync(BooleanFromString)(value);
    return acc;
  });

const urlRecordToCheckboxFilterStateMap = (
  urlRecord: Record<string, string>
): CheckboxFilterStateMap => {
  let result = HashMap.empty<AttributeCode, CheckboxFilterValue>();

  for (const [key, value] of Object.entries(urlRecord)) {
    if (!isCheckboxParam(key)) continue;

    const code = extractCheckboxCode(key);

    const booleanResult = Schema.decodeUnknownExit(BooleanFromString)(value);

    if (Exit.isSuccess(booleanResult)) {
      result = HashMap.set(result, code, booleanResult.value);
    }
    // Silently ignore invalid boolean formats (not "true" or "false")
  }

  return result;
};

export const UrlQueryRecord = Schema.Record(Schema.String, Schema.String);

export const CheckboxToUrl = Schema.toType(CheckboxFilterStateMap).pipe(
  Schema.decodeTo(UrlQueryRecord, {
    decode: SchemaGetter.transform(checkboxFilterStateMapToUrlRecord),
    encode: SchemaGetter.transform(urlRecordToCheckboxFilterStateMap),
  })
);
